import { authenticatedFetch } from "./authenticatedFetch";
import { truncateMarkdownForNeoAi } from "utils/neoAiApplyEdit";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/** POST `/ai/ask` (override with `VITE_AI_ASK_PATH` if your gateway prefix differs). */
export const AI_ASK_PATH = import.meta.env.VITE_AI_ASK_PATH ?? "/ai/ask";

/** POST `/ai/ask-template` for template editor NeoAi. */
export const AI_ASK_TEMPLATE_PATH =
  import.meta.env.VITE_AI_ASK_TEMPLATE_PATH ?? "/ai/ask-template";

/**
 * Fire-and-forget POST with empty `prompt` to warm up cold Lambdas.
 * Ignores response body and errors (best-effort only).
 */
export function warmupAiAsk() {
  void authenticatedFetch(`${API_BASE_URL}${AI_ASK_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt: "" }),
  }).catch(() => {});
}

/** Warm up `/ai/ask-template` (template editor). */
export function warmupAiAskTemplate() {
  void authenticatedFetch(`${API_BASE_URL}${AI_ASK_TEMPLATE_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt: "" }),
  }).catch(() => {});
}

function formatHttpError(payload) {
  if (payload == null) return "Não foi possível completar o pedido.";
  if (typeof payload === "string") return payload;
  if (typeof payload === "object") {
    return (
      payload.error ||
      payload.message ||
      payload.detail ||
      "Não foi possível completar o pedido."
    );
  }
  return "Não foi possível completar o pedido.";
}

/**
 * @param {{ prompt: string; documentMarkdown?: string }} params
 * @returns {Promise<
 *   | { ok: true; response: string; edit?: object | null; coinBalance?: string | number }
 *   | { ok: false; message: string }
 * >}
 */
export async function askAiAgent({ prompt, documentMarkdown }) {
  const trimmed = String(prompt ?? "").trim();
  if (!trimmed) {
    return { ok: false, message: "Digite um pedido para a NeoAi." };
  }

  const docRaw = typeof documentMarkdown === "string" ? documentMarkdown.trim() : "";
  const docForApi = docRaw.length > 0 ? truncateMarkdownForNeoAi(docRaw) : null;

  const body = { prompt: trimmed };
  if (docForApi) {
    body.document_markdown = docForApi;
  }

  try {
    const response = await authenticatedFetch(`${API_BASE_URL}${AI_ASK_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const message = formatHttpError(data) || `Erro ao chamar o assistente (${response.status}).`;
      return { ok: false, message };
    }

    if (!data || typeof data !== "object") {
      return { ok: false, message: "Resposta inválida do servidor." };
    }

    if (data.valid === true) {
      const md = typeof data.response === "string" ? data.response : "";
      const coinRaw = data.coin_balance ?? data.coinBalance;
      const hasCoin =
        coinRaw !== undefined &&
        coinRaw !== null &&
        !(typeof coinRaw === "string" && coinRaw.trim() === "");
      /** @type {{ ok: true; response: string; edit?: object | null; coinBalance?: string | number }} */
      const okResult = { ok: true, response: md.trim(), edit: data.edit ?? null };
      if (hasCoin) {
        okResult.coinBalance = coinRaw;
      }
      return okResult;
    }

    const errMsg =
      (typeof data.error === "string" && data.error.trim()) ||
      (typeof data.message === "string" && data.message.trim()) ||
      "O assistente não pôde atender ao pedido.";

    return { ok: false, message: errMsg };
  } catch (e) {
    console.error("askAiAgent:", e);
    return {
      ok: false,
      message: "Erro de rede. Verifique sua conexão e tente novamente.",
    };
  }
}

/**
 * @param {{ prompt: string; documentMarkdown?: string }} params
 * @returns {Promise<
 *   | { ok: true; response: string; edit?: object | null; coinBalance?: string | number }
 *   | { ok: false; message: string }
 * >}
 */
export async function askAiTemplateAgent({ prompt, documentMarkdown }) {
  const trimmed = String(prompt ?? "").trim();
  if (!trimmed) {
    return { ok: false, message: "Digite um pedido para a NeoAi." };
  }

  const docRaw = typeof documentMarkdown === "string" ? documentMarkdown.trim() : "";
  const docForApi = docRaw.length > 0 ? truncateMarkdownForNeoAi(docRaw) : null;

  const body = { prompt: trimmed };
  if (docForApi) {
    body.document_markdown = docForApi;
  }

  try {
    const response = await authenticatedFetch(`${API_BASE_URL}${AI_ASK_TEMPLATE_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      const message = formatHttpError(data) || `Erro ao chamar o assistente (${response.status}).`;
      return { ok: false, message };
    }

    if (!data || typeof data !== "object") {
      return { ok: false, message: "Resposta inválida do servidor." };
    }

    if (data.valid === true) {
      const md = typeof data.response === "string" ? data.response : "";
      const coinRaw = data.coin_balance ?? data.coinBalance;
      const hasCoin =
        coinRaw !== undefined &&
        coinRaw !== null &&
        !(typeof coinRaw === "string" && coinRaw.trim() === "");
      /** @type {{ ok: true; response: string; edit?: object | null; coinBalance?: string | number }} */
      const okResult = { ok: true, response: md.trim(), edit: data.edit ?? null };
      if (hasCoin) {
        okResult.coinBalance = coinRaw;
      }
      return okResult;
    }

    const errMsg =
      (typeof data.error === "string" && data.error.trim()) ||
      (typeof data.message === "string" && data.message.trim()) ||
      "O assistente não pôde atender ao pedido.";

    return { ok: false, message: errMsg };
  } catch (e) {
    console.error("askAiTemplateAgent:", e);
    return {
      ok: false,
      message: "Erro de rede. Verifique sua conexão e tente novamente.",
    };
  }
}
